import { createStyles } from '@mantine/core';
import type { ReactNode } from 'react';

import { Avatar } from '~/domains/design-system/Avatar';
import { Text } from '~/domains/design-system/Text';

import { formatHandle } from '../../utils/formatHandle';

const useStyles = createStyles((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._12,
    padding: `${theme.other.spacing._8}px 0`,
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._12,
    flexGrow: 1,
    minWidth: 0,
    padding: 0,
    border: 0,
    background: 'transparent',
    textAlign: 'left',
  },
  identity: { display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 },
  label: {
    color: theme.colors.gray2[6],
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._16,
    flexShrink: 0,
  },
  clickable: { cursor: 'pointer' },
  notClickable: { cursor: 'default' },
}));

type GroupUserRowProps = {
  handle: string;
  /** Overrides the handle as the row's label — used for the viewer's own "You" row. */
  label?: string;
  avatarUrl?: string;
  subtitle?: ReactNode;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  className?: string;
  onClick?: () => void;
  'data-test-id'?: string;
  'aria-disabled'?: boolean;
};

export function GroupUserRow({
  handle,
  label,
  avatarUrl,
  subtitle,
  leftSection,
  rightSection,
  className,
  onClick,
  'data-test-id': dataTestId,
  'aria-disabled': ariaDisabled,
}: GroupUserRowProps) {
  const { classes, cx } = useStyles();
  return (
    <div className={cx(classes.row, className)}>
      {leftSection}
      <button
        type="button"
        className={cx(classes.rowContent, onClick ? classes.clickable : classes.notClickable)}
        onClick={onClick}
        data-test-id={dataTestId}
        aria-disabled={ariaDisabled}
      >
        <Avatar handle={handle} src={avatarUrl} size={36} />
        <div className={classes.identity}>
          <Text variant="title-medium" className={classes.label}>
            {label ?? formatHandle(handle)}
          </Text>
          {subtitle}
        </div>
      </button>
      {rightSection && <div className={classes.rightSection}>{rightSection}</div>}
    </div>
  );
}
